<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

// BonBoZ: gán mẫu trả lời theo trang — một kho mẫu chung, danh sách gán trang
// nằm ở BAG cấu hình của service CRM (không thêm cột, không migration Rails).
import CannedByPage from './CannedByPage.vue';
// BonBoZ: chu de co mau + gan mot loat mau cho nhieu trang.
import QuanLyChuDe from './QuanLyChuDe.vue';
import GanNhieuTrang from './GanNhieuTrang.vue';
import ChipChuDe from './ChipChuDe.vue';
import { napChuDe } from 'dashboard/helper/bonboz/chuDeMau';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

// BonBoZ: modal gán mẫu theo trang
const showGanTrang = ref(false);
const showChuDe = ref(false);
const showGanNhieu = ref(false);
// Tick chon nhieu mau. Set de bat/tat mot dong khong phai quet ca mang.
const daTick = ref(new Set());
const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const searchQuery = ref('');

const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'short_code', weight: 4 },
    'content',
  ]);
});
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
  napChuDe();
});

// BonBoZ: tick chon nhieu mau -> gan cho nhieu trang mot luot.
const doiTick = id => {
  const next = new Set(daTick.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  daTick.value = next; // gan lai de Vue nhan ra thay doi
};
const boTick = () => {
  daTick.value = new Set();
};
// Tick het / bo het theo DANH SACH DANG LOC, khong phai toan kho: nguoi dung
// vua tim "gia" xong bam chon het la muon nhung mau dang thay.
const tickHet = () => {
  const ids = filteredRecords.value.map(m => m.id);
  daTick.value = ids.every(id => daTick.value.has(id))
    ? new Set()
    : new Set(ids);
};
const ganXong = () => {
  showGanNhieu.value = false;
  boTick();
};

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('CANNED_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CANNED_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('CANNED_MGMT.HEADER')"
        :description="$t('CANNED_MGMT.DESCRIPTION')"
        :link-text="$t('CANNED_MGMT.LEARN_MORE')"
        :search-placeholder="$t('CANNED_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="canned_responses"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('CANNED_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <!-- BonBoZ: lối vào màn gán mẫu theo trang -->
          <Button
            :label="$t('BONBOZ.CHU_DE.HEADER_BTN')"
            size="sm"
            slate
            faded
            @click="showChuDe = true"
          />
          <Button
            :label="$t('BONBOZ.MAU_TRANG.HEADER_BTN')"
            size="sm"
            slate
            faded
            @click="showGanTrang = true"
          />
          <Button
            :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <!-- BonBoZ: thanh nay chi hien khi da tick, de cho trong bang luc binh
           thuong khong doi. -->
      <div
        v-if="daTick.size"
        class="flex flex-wrap items-center gap-3 px-4 py-2 mb-2 border rounded-lg border-n-brand bg-n-brand/5"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('BONBOZ.GAN_NHIEU.DA_TICK', { so: daTick.size }) }}
        </span>
        <Button
          size="sm"
          :label="$t('BONBOZ.GAN_NHIEU.NUT')"
          @click="showGanNhieu = true"
        />
        <Button
          size="sm"
          slate
          faded
          :label="$t('BONBOZ.GAN_NHIEU.BO_TICK')"
          @click="boTick"
        />
      </div>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          !records.length
            ? $t('CANNED_MGMT.LIST.404')
            : searchQuery
              ? $t('CANNED_MGMT.NO_RESULTS')
              : ''
        "
      >
        <template #header-0>
          <span class="flex items-center gap-2">
            <input
              type="checkbox"
              class="!mb-0 shrink-0"
              :aria-label="$t('BONBOZ.GAN_NHIEU.TICK_HET')"
              :checked="
                filteredRecords.length > 0 &&
                filteredRecords.every(m => daTick.has(m.id))
              "
              @change="tickHet"
            />
            <button
              class="flex items-center gap-2 p-0 cursor-pointer"
              @click="toggleSort"
            >
              <span class="mb-0">
                {{ tableHeaders[0] }}
              </span>
              <Icon
                class="size-5 text-n-slate-11 flex-shrink-0"
                :icon="
                  sortOrder === 'desc'
                    ? 'i-woot-sort-descending'
                    : 'i-woot-sort-ascending'
                "
              />
            </button>
          </span>
        </template>
        <template #header-1>
          {{ tableHeaders[1] }}
        </template>

        <template #row="{ items }">
          <BaseTableRow
            v-for="cannedItem in items"
            :key="cannedItem.short_code"
            :item="cannedItem"
          >
            <template #default>
              <BaseTableCell class="max-w-0">
                <div class="flex flex-col gap-2 min-w-0">
                  <span class="flex items-center gap-2 min-w-0">
                    <input
                      type="checkbox"
                      class="!mb-0 shrink-0"
                      :checked="daTick.has(cannedItem.id)"
                      :aria-label="cannedItem.short_code"
                      @change="doiTick(cannedItem.id)"
                    />
                    <span class="text-heading-3 text-n-slate-12 truncate">
                      {{ cannedItem.short_code }}
                    </span>
                    <ChipChuDe :id="cannedItem.chu_de_id" />
                  </span>
                  <p class="text-body-main text-n-slate-11 line-clamp-5">
                    {{ getPlainText(cannedItem.content) }}
                  </p>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEditPopup(cannedItem)"
                  />
                  <Button
                    v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[cannedItem.id]"
                    @click="openDeletePopup(cannedItem)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>
    <!-- BonBoZ: gán mẫu theo trang; `records` là kho mẫu cấp tài khoản -->
    <CannedByPage
      v-if="showGanTrang"
      :mau="records"
      @close="showGanTrang = false"
    />
    <QuanLyChuDe v-if="showChuDe" @close="showChuDe = false" />
    <GanNhieuTrang
      v-if="showGanNhieu"
      :mau-ids="[...daTick]"
      @close="showGanNhieu = false"
      @xong="ganXong"
    />

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :edanh-ids="activeResponse.anh_ids || []"
        :edanh="activeResponse.anh || []"
        :edtin-them="activeResponse.tin_them || []"
        :edchu-de-id="activeResponse.chu_de_id || ''"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
